import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Popup } from "react-mapbox-gl";

import {
  allDepotListSelector,
  pageToShowIndexSelector,
  simulatedVolumeOverlayDataSelector,
} from "../../../reducers/app/selector";
import {
  isMapAdjustingSelector,
  showManifestLocationStopsSelector,
} from "../../../reducers/map/selector";
import { businessUnitFilterSelector } from "../../../reducers/depot/selector";
import { LocationIcon } from "../../../assets/icons/Location";
import { DEPOT, OUTER, SECTOR } from "../../../constants/layers";
import { ArrowIcon } from "../../../assets/icons/MapArrowIcon";
import "./Layers.css";
import {
  isMarkerVisible,
  setUpBoundariesWithDepotKeys,
  getCenterOfDepot,
} from "../../../utils/map";
import { Postcode } from "../../../utils/postcode";

export const PopupLayer = ({
  map,
  parcelMapData,
  mapLevel,
  districtBoundaries,
  sectorBoundaries,
  manifestLocations,
}) => {
  const [labelMapBounds, setlabelMapBounds] = useState(map.getBounds()); // Need local state to stop rerender of whole map
  const simulatedList = useSelector(simulatedVolumeOverlayDataSelector);
  const isMapAdjusting = useSelector(isMapAdjustingSelector);
  const allDepots = useSelector(allDepotListSelector);
  const pageToShowIndex = useSelector(pageToShowIndexSelector);
  const businessUnitFilter = useSelector(businessUnitFilterSelector);
  const showManifestLocations = useSelector(showManifestLocationStopsSelector);

  const depotBoundaries = setUpBoundariesWithDepotKeys(districtBoundaries);

  useEffect(() => {
    setlabelMapBounds(map.getBounds());
  }, [isMapAdjusting, map]); // Forces map to rerender on drag which updates the markers

  let boundariesList = [];
  let depotParcelData = [];

  const manifestLocationPopups = () => {
    return manifestLocations.map(
      ({ locationCode, postcode, longitude, latitude }) => {
        const postcodeObj = new Postcode(postcode);
        const district = postcodeObj.toOuter();
        const sector = postcodeObj.toSector();

        const manifestLocationSectorData = parcelMapData.find(
          ({ value }) => value === district || value === sector
        );

        const manifestLocationStops =
          manifestLocationSectorData?.manifestLocationStops?.[locationCode]
            ?.mlStops;

        const manifestLocationSimulatedStops =
          manifestLocationSectorData?.manifestLocationStops?.[locationCode]
            ?.mlSimStops;

        return (
          map && (
            <div key={`${locationCode} popup`}>
              {manifestLocationSectorData && manifestLocationStops > 0 && (
                <Popup coordinates={[latitude, longitude]} anchor={"bottom"}>
                  {manifestLocationStops}
                  {manifestLocationSimulatedStops > manifestLocationStops &&
                    simulatedList !== null &&
                    pageToShowIndex === 0 && (
                      <div className="popupSimulatedContainer">
                        <ArrowIcon /> {manifestLocationSimulatedStops}
                      </div>
                    )}
                  <LocationIcon className={"popupIcon"} />
                </Popup>
              )}
            </div>
          )
        );
      }
    );
  };

  if (mapLevel === SECTOR) {
    boundariesList = sectorBoundaries;
  } else if (mapLevel === OUTER || pageToShowIndex === 0) {
    // Stops rendering any depot level code if on Simulation page
    boundariesList = districtBoundaries;
  } else if (mapLevel === DEPOT) {
    boundariesList = allDepots.filter(
      (depot) => depot.businessUnit === businessUnitFilter
    );
    parcelMapData.forEach(({ depotCode, stops }) => {
      const depotIndex = depotParcelData.findIndex((depot) => {
        return depot.value === depotCode;
      });
      const boundaryIndex = boundariesList.findIndex((boundary) => {
        return boundary.depotCode === depotCode;
      });
      if (depotIndex === -1 && boundaryIndex !== -1) {
        depotParcelData.push({ stops, value: depotCode, depotCode });
      } else if (depotIndex > -1 && boundaryIndex !== -1) {
        depotParcelData[depotIndex].stops += stops;
      }
    });
    parcelMapData = depotParcelData;
  }

  let value;
  let popupCenter;
  let parcelIndex;
  return (
    <>
      {parcelMapData.length > 0 && (
        <>
          {boundariesList
            .filter(({ center, depotName, depotCode }) => {
              if (depotName) {
                popupCenter = getCenterOfDepot(depotBoundaries[depotCode]);
              } else {
                popupCenter = center;
              }

              return isMarkerVisible(popupCenter, labelMapBounds);
            })
            .map(({ depotCode, depotName, center, postcode }) => {
              if (depotName) {
                value = depotCode;
                popupCenter = getCenterOfDepot(depotBoundaries[depotCode]);
              } else {
                value = postcode;
                popupCenter = center;
                parcelIndex = parcelMapData.findIndex((mapPostcode) => {
                  return mapPostcode.value === value;
                });
              }

              if (pageToShowIndex === 1) {
                parcelIndex = parcelMapData.findIndex((mapPostcode) => {
                  return (
                    mapPostcode.value === value &&
                    mapPostcode.depotCode === depotCode
                  );
                });
              }

              const coords = [popupCenter[0], popupCenter[1]];

              if (!parcelMapData[parcelIndex]) return null;

              const { stops, totalMlStops, simStops, totalMlSimStops } =
                parcelMapData[parcelIndex];
              const totalStops = showManifestLocations
                ? stops - totalMlStops
                : stops;

              const totalSimulatedStops = showManifestLocations
                ? simStops - totalMlSimStops
                : simStops;

              return (
                map &&
                totalStops > 0 && (
                  <Popup
                    coordinates={coords}
                    anchor={"bottom"}
                    styles={{ pointerEvents: "none" }}
                    interactive={false}
                    key={`${value} popup`}
                  >
                    {totalStops}
                    {parcelMapData[parcelIndex] &&
                      simulatedList !== null &&
                      pageToShowIndex === 0 && (
                        <div className="popupSimulatedContainer">
                          <ArrowIcon /> {totalSimulatedStops}
                        </div>
                      )}
                    <LocationIcon className={"popupIcon"} />
                  </Popup>
                )
              );
            })}
          {showManifestLocations && manifestLocationPopups()}
        </>
      )}
    </>
  );
};
